import { Trans } from '@lingui/react/macro';
import React from 'react';
import UDText from 'ud-ui/components/text';
import UDCard from 'ud-ui/components/card';

export const OPERATOR_MARKER_EXPLAINER_ID = 'video-markers-guide-hotkey';

/**
 * Зачем оператору клавиша метки и что будет при разных нажатиях. Общий для OBS и vMix: поведение
 * сверено с адаптерами (`obs-recorder-clock.ts`, `vmix-recorder-clock.ts`) и хуком `use-video-markers`.
 */
function OperatorMarkerExplainer() {
  return (
    <UDCard
      as="section"
      variant="plain"
      padding="lg"
      id={OPERATOR_MARKER_EXPLAINER_ID}
      className="flex flex-col gap-3"
      aria-labelledby={`${OPERATOR_MARKER_EXPLAINER_ID}-title`}
    >
      <UDText as="h2" variant="title-4" id={`${OPERATOR_MARKER_EXPLAINER_ID}-title`}>
        <Trans>Клавиша метки: зачем она и как работает</Trans>
      </UDText>

      <UDText variant="body-2">
        <Trans>
          Метки ставятся сами на каждое событие, которое статист отмечает в приложении: гол, карточку, замену. Клавиша метки (например,
          F9) — для всего остального:
        </Trans>
      </UDText>
      <ul className="flex flex-col gap-1 pl-2">
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>
              <b>Моменты, которых нет в приложении</b>: удар в штангу, опасный момент у ворот, стычка, реакция трибун — всё, что
              пригодится для нарезки.
            </Trans>
          </UDText>
        </li>
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>
              <b>Подстраховка</b>: статист пропустил событие, внёс его с опозданием или у его телефона пропала связь.
            </Trans>
          </UDText>
        </li>
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>
              <b>Точность</b>: метка встаёт в момент нажатия — без задержки, пока статист открывает экран события и сохраняет его.
            </Trans>
          </UDText>
        </li>
      </ul>

      <UDText as="h3" variant="subtitle-1-bold">
        <Trans>Что происходит при нажатии</Trans>
      </UDText>
      <ul className="flex flex-col gap-1 pl-2">
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>
              <b>Одно нажатие — одна метка</b>: секунда записи (у OBS — ещё и эфира) в момент нажатия и время матча. Метка не привязана
              ни к какому событию — это закладка на видео. В OBS она дополнительно попадает главой в файл записи, в vMix — строкой в
              лог записи.
            </Trans>
          </UDText>
        </li>
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>
              <b>Нажали несколько раз</b> — будет несколько меток, ничего не сломается; лишние потом просто не используют. В vMix
              нажатия чаще раза в полсекунды сливаются в одну метку.
            </Trans>
          </UDText>
        </li>
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>
              <b>Нажали до события или через несколько секунд после</b> — метка встанет ровно в момент нажатия, а событие из приложения
              получит свою отдельную метку. Они не заменяют и не сдвигают друг друга. Жмите, когда видите момент, а не когда статист
              его внесёт.
            </Trans>
          </UDText>
        </li>
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>
              <b>Метка не ставится</b>, если табло в этот момент не подключено к программе — в панели видеометок нет надписи
              «подключён». Если не идут ни запись, ни эфир, метка сохранится без секунды в видео.
            </Trans>
          </UDText>
        </li>
        <li className="flex gap-2">
          <UDText variant="body-2" aria-hidden>
            •
          </UDText>
          <UDText variant="body-2">
            <Trans>Клавиша OBS работает и при свёрнутом OBS. Клавиша vMix — только когда окно vMix активно.</Trans>
          </UDText>
        </li>
      </ul>
    </UDCard>
  );
}

export default React.memo(OperatorMarkerExplainer);
